<template>
  <div :class="{'auth-google-slide': integrationPlatform === 'GoogleSlide'}">
    <public-announcement />
    <div class="flex items-center justify-center min-h-screen p-4 overflow-hidden">
      <div class="relative z-10 w-108">
        <MultipleLanguage />

        <div class="flex justify-center">
          <span class="pt-10 pb-14" v-if="!isPresenterWhiteLabel">
            <a href="https://ahaslides.com">
              <aha-logo
                width="140"
                height="32"
              />
            </a>
          </span>
        </div>

        <!-- Form -->
        <div class="shadow-panel rounded-3xl bg-base-0 px-9 pt-10 pb-12">
          <h2 class="mb-4 text-center text-2xl w-80 mx-auto" v-if="isTrialLink">
            {{ $i18nPresenter.t('Log in to get AhaSlides One-month Free trial') }}
          </h2>
          <h2 class="mb-4 text-center text-2xl" v-else>
            {{ $i18nPresenter.t('Log in') }}
          </h2>
          <div v-if="!isPresenterWhiteLabel">
            <p class="text-sm text-center mb-8">
              {{ $i18nPresenter.t('No account?') }}
              <a @click="goSignup()" class="font-bold text-link--primary">
                {{ $i18nPresenter.t('Sign up now') }}
              </a>
            </p>

            <ThirdPartyButton type="login" provider="google" class="mb-3">
              {{ $i18nPresenter.t('Log in with Google') }}
            </ThirdPartyButton>

            <ThirdPartyButton
              type="login"
              provider="microsoft"
              v-if="integrationPlatform !== 'GoogleSlide'"
            >
              {{ $i18nPresenter.t('Log in with Microsoft') }}
            </ThirdPartyButton>

            <div class="separator my-6">
              {{ $i18nPresenter.t('or') }}
            </div>
          </div>
          <form @submit.prevent="onSubmitLogin">
            <aha-input-group class="mt-6 mb-4">
              <span class="aha-input-prepend">
                <aha-icon name="mail" />
              </span>

              <aha-input
                type="email"
                class="w-full"
                :placeholder="$i18nPresenter.t('Your email')"
                required
                v-model="email"
              />
            </aha-input-group>

            <aha-input-group class="relative mb-2">
              <span class="aha-input-prepend">
                <aha-icon name="lock" />
              </span>

              <aha-input
                :type="showPassword ? 'text' : 'password'"
                class="w-full aha-input_limit-char"
                :placeholder="$i18nPresenter.t('Your password')"
                size="md"
                required
                v-model="password"
              />

              <span
                class="absolute flex items-center cursor-pointer inset-y-2 right-3"
                @click="showPassword = !showPassword"
              >
                <font-awesome-icon
                  class="text-base-80"
                  :icon="!showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                />
              </span>
            </aha-input-group>

            <p class="text-sm mb-6">
              <router-link
                :to="routerLinkForgotPasswordPage"
                class="text-base-70 underline text-underline-offset"
              >
                {{ $i18nPresenter.t('Forgot password?') }}
              </router-link>
            </p>

            <div class="mb-4 text-sm text-red-100" v-if="textError">
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
              />
              <span v-if="textError === 'default_error'">
                {{ $i18nPresenter.t("Oops, there's an error while logging you in. Please try again.") }}
                <span
                  v-html="$i18nPresenter.t('ifTheProblemPersists')"
                ></span>
              </span>
              <span v-else>
                {{ textError }}
              </span>
            </div>

            <div class="text-center form-group mb-4">
              <div class="col-xs-12">
                <aha-antd-button
                  v-aha-emit-action
                  name="login-button"
                  type="submit"
                  class="w-full"
                  size="lg"
                  :submitting="submitting"
                  is-loading-inline
                  :disabled="isDisabled"
                >
                  <span>{{ $i18nPresenter.t('Log In') }}</span>
                </aha-antd-button>
              </div>
            </div>

            <p class="text-center" v-if=" !$store.state.isPresenterWhiteLabel && integrationPlatform !== 'GoogleSlide'">
              <a
                class="font-bold text-link--primary"
                @click="handleLoginWithSso()"
              >
                {{ $i18nPresenter.t('Log in with SSO') }}
              </a>
            </p>
          </form>
        </div>

        <span class="absolute bottom-0 hidden lg:block -right-20">
          <img src="@/assets/images/elements/tree.svg" alt="tree">
        </span>

        <div class="h-28" />
      </div>

      <back-drop />

      <span class="absolute hidden lg:block top-1/4 right-2/3">
        <img src="@/assets/images/elements/music-note.svg" alt="music-note">
      </span>

      <span class="absolute hidden lg:block top-2/4 left-1/4">
        <img src="@/assets/images/elements/pallet.svg" alt="pallet">
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'

import { authentication, app as msTeamsApp } from '@microsoft/teams-js'
import Util from '@/util'
import Config from '@/config'
import recaptchaV3Service from '@/services/recaptcha-v3'
import cookie from '@/services/cookie'
import { PRESENTER_LOGIN } from '@/constant/eventCategories'
import { AHA_GUEST_USER } from '@/constant/storageKeys'
import {
  isTrialLink, scheduleShowEnjoyTrialPopup, scheduleShowTrialNotAvailablePopup, isPlanDifferentFromTrial,
} from '@/utils/trial-plan.utils'

import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import BackDrop from '@/components-v2/common/auth/BackDrop.vue'
import ThirdPartyButton from '@/components-v2/common/auth/ThirdPartyButton.vue'
import BulkLicenseMixin from '@/mixins/BulkLicenseMixin.js'
import MultipleLanguage from '@/components-v2/multiple-language/MultipleLanguage.vue'
import UnleashService from '@/services/unleash'
import office from '@/services/office'

export default {
  name: 'Login',
  components: {
    BackDrop,
    ThirdPartyButton,
    PublicAnnouncement,
    MultipleLanguage,
  },
  mixins: [BulkLicenseMixin],
  data() {
    return {
      email: '',
      password: '',
      checkbox_remember_me: true,
      textError: '',
      submitting: false,
      keyRecaptcha: Config.keyRecapcha,
      tokenRecaptcha: '',
      showPassword: false,
      isGuest: false,
      isFromMSTeams: false,
      isFromZoomApp: false,
      isTrialLink: false,
      isCaptcharLoadError: false,
      isCanloadRecaptcha: false,

    }
  },
  computed: {
    ...mapState({
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      isPowerPointAddIn: (state) => state.isPowerPointAddIn,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      integrationPlatform: (state) => state.integrationPlatform,
    }),
    inviterName() {
      return Util.getParamValue('inviter-name', window.location.href)
    },
    inviterEmail() {
      return Util.getParamValue('inviter-email', window.location.href)
    },
    sharedPresentationName() {
      return Util.getParamValue('shared-presentation', window.location.href)
    },
    isDisabled() {
      return !(this.email || this.password)
    },
    routerLinkForgotPasswordPage() {
      return {
        path: '/pages/forgot-password',
        query: {
          ...this.$route.query,
          ms_teams: this.isFromMSTeams ? true : undefined,
        },
      }
    },
  },
  async created() {
    try {
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
    } catch (error) {
      Sentry.captureException(new Error('Load unleash login error'), {
        extra: {
          error,
        },
      })
    }
    this.isCanloadRecaptcha = true
    this.checkZoomAppAction()
    this.isTrialLink = isTrialLink(window.location.href)
    this.isGuest = !!cookie.getValue(AHA_GUEST_USER)

    if (Util.getParamValue('bl_checkout', window.location.href)) {
      const canRedirectToBulkLicenseCheckoutPage = await this.canRedirectToBulkLicenseCheckoutPage()
      if (canRedirectToBulkLicenseCheckoutPage) {
        return
      }
    }

    await this.initializeForIntegratedApps()

    if (cookie.getValue('ahaToken') && !this.isGuest) {
      const redirect = Util.getParamValue('redirect', window.location.href)
      if (this.isFromMSTeams) {
        authentication.notifySuccess()
      }
      if (redirect) {
        window.location.href = redirect
        return
      }

      if (this.integrationPlatform === 'GoogleSlide') {
        return
      }
      this.$router.push('/apps/home')
    }
  },
  mounted() {
    document.body.classList.remove('no-scroll')
    Util.mixpanelTracking(
      PRESENTER_LOGIN,
      'Open login form',
    )
  },
  methods: {
    ...mapActions([
      'createCheckoutSessionAction',
      'copyDemoPresentationAfterLoggingInAction',
    ]),
    ...mapActions('zoomApp', [
      'checkZoomAppAction',
    ]),
    onVerify(response) {
      this.tokenRecaptcha = response
      this.login()
    },
    async onSubmitLoginWithRecaptchaV3() {
      try {
        this.tokenRecaptcha = await recaptchaV3Service.executeRecaptcha('login')
        if (!this.tokenRecaptcha) {
          this.isCaptcharLoadError = true
        }
        this.login()

        Util.trackGA4AndMixpanel(
          PRESENTER_LOGIN,
          'reCAPTCHA v3 verified',
        )
      } catch (error) {
        this.isCaptcharLoadError = true
        this.submitting = false
        this.textError = 'Recaptcha error. If the problem still occurs, please try again.'

        Sentry.captureException(new Error(`Presenter login with reCAPTCHA v3 error: ${this.email}`), {
          extra: { error: error.message },
        })
      }
    },
    onSubmitLogin() {
      if (this.submitting) return
      this.submitting = true
      if (Config.env === 'local' || this.email.match(/qa\.ahaslides\+(.*)@gmail.com/i) || this.isCaptcharLoadError || this.isPowerPointAddIn) {
        this.login()
      } else {
        this.onSubmitLoginWithRecaptchaV3()
      }
    },

    setLang(lang) {
      this.$store.dispatch('setLang', lang)
    },

    onRecaptchaError() {
      this.isCaptcharLoadError = true
      this.submitting = false
      // this.textError = 'Recaptcha error. If the problem still occurs, please check your internet connection.'
    },

    async initializeForMSTeamsApp() {
      const isFromMSTeams = !!Util.getParamValue('ms_teams', window.location.href)

      this.isFromMSTeams = isFromMSTeams
      if (!this.isFromMSTeams) return

      localStorage.setItem('ms_teams', 'true')
      await msTeamsApp.initialize()
    },

    async initializeForZoomApp() {
      const isFromZoomApp = !!Util.getParamValue('zoomapp', window.location.href)
      this.isFromZoomApp = isFromZoomApp

      if (!this.isFromZoomApp) return
      const loginRedirectUrl = Util.getParamValue('redirect', window.location.href)

      if (!loginRedirectUrl) return
      sessionStorage.setItem('zoomAppLoginRedirectUrl', loginRedirectUrl)
    },

    async initializeForIntegratedApps() {
      await this.initializeForMSTeamsApp()
      await this.initializeForZoomApp()
    },

    goSignup() {
      const content = Util.getParamValue('content', window.location.href)
      const source = Util.getParamValue('source', window.location.href)
      const promo = Util.getParamValue('promo', window.location.href)
      const redirect = Util.getParamValue('redirect', window.location.href)
      const code = Util.getParamValue('code', window.location.href)
      const isBulkLicenseCheckout = Util.getParamValue('bl_checkout', window.location.href)
      const from = Util.getParamValue('from', window.location.href)

      let query = {}

      if (content && source === 'pricing') {
        query = {
          content,
          source,
        }

        if (promo) {
          query.promo = promo
        }
      }

      if (this.isFromMSTeams) {
        query.ms_teams = 'true'
      }

      if (redirect) {
        query.redirect = redirect
      }
      if (code) {
        query.code = code
      }
      if (from) {
        query.from = from
      }

      if (isBulkLicenseCheckout) {
        query.bl_checkout = isBulkLicenseCheckout
        query.bl_plan = Util.getParamValue('bl_plan', window.location.href)
        query.bl_qty = Util.getParamValue('bl_qty', window.location.href)
      }

      this.$router.push({
        path: '/pages/register',
        query,
      })
    },

    async login() {
      // eslint-disable-next-line promise/catch-or-return

      const payload = {
        email: this.email,
        password: this.password,
        tokenRecaptcha: this.tokenRecaptcha,
        isTrial: this.isTrialLink,
        source: window.location.href,
      }
      if (this.isPowerPointAddIn || this.isCaptcharLoadError || Config.env === 'local') {
        payload.bypassRecaptcha = true
      }
      payload.isRecaptchaV3 = true

      try {
        const res = await this.$http.post(Config.api.login, payload)
        this.submitting = false
        Util.setCookieToken(res.body.token)

        Object.assign(this.$http.headers, {
          common: { Authorization: `Bearer ${res.body.token}` },
        })
        this.$store.dispatch('setCurrentUserAction', res.body.user)

        if (this.isGuest) {
          cookie.deleteValue(AHA_GUEST_USER)
          await this.copyDemoPresentationAfterLoggingInAction({
            shareCode: Util.getParamValue('code', window.location.href),
          })
          return
        }

        const redirect = Util.getParamValue('redirect', window.location.href)

        if (this.$route.query.from !== 'audience') {
          Util.mixpanelTracking(
            PRESENTER_LOGIN,
            'Login Button > Submit > Succeeded',
            '',
          )

          Util.trackGA4AndMixpanel('login', {
            method: 'with form login',
          })
        } else {
          Util.handleAudienceTrackingAuthentication(this.isGuest, false, 'form login', redirect)
        }

        const { user } = res.body
        Util.ga4Config({
          user_id: user.id,
        })

        const content = Util.getParamValue('content', window.location.href)
        const source = Util.getParamValue('source', window.location.href)
        if (this.isFromMSTeams) {
          authentication.notifySuccess(res.body.token)
        }

        const canRedirectToBulkLicenseCheckoutPage = await this.canRedirectToBulkLicenseCheckoutPage(user)

        if (canRedirectToBulkLicenseCheckoutPage) {
          return
        }
        if (this.isTrialLink) {
          if (res.body.user.hasUpgradedToTrial) {
            scheduleShowEnjoyTrialPopup(res.body.user)
          } else if (isPlanDifferentFromTrial(res.body.user)) {
            scheduleShowTrialNotAvailablePopup(res.body.user)
          }
        }

        let isPlan = false
        if (
          content === 'essential_annually' ||
          content === 'pro_annually' ||
          content === 'essential_monthly' ||
          content === 'pro_monthly' ||
          content === 'plus_monthly' ||
          content === 'plus_annually' ||
          content === 'edu_small' ||
          content === 'edu_medium' ||
          content === 'edu_large' ||
          content === 'one_time_mini' ||
          content === 'one_time_small' ||
          content === 'one_time_medium' ||
          content === 'one_time_large'
        ) {
          isPlan = true
        }

        // eslint-disable-next-line promise/always-return
        if (redirect) {
          window.location.href = redirect
        } else if (source === 'pricing' && isPlan) {
          const planMapping = {
            essential_annually: 'essential-yearly',
            essential_monthly: 'essential-monthly',
            pro_monthly: 'pro-monthly',
            plus_monthly: 'plus-monthly',
            plus_annually: 'plus-yearly',
            edu_small: 'edu-small-yearly',
            edu_medium: 'edu-medium-yearly',
            edu_large: 'edu-large-yearly',
            one_time_mini: 'one-time-mini',
            one_time_small: 'one-time-small',
            one_time_medium: 'one-time-medium',
            one_time_large: 'one-time-large',
          }

          const plan = planMapping[content] || 'pro-yearly'
          const promo = Util.getParamValue('promo', window.location.href)
          let urlRedirect = `${Config.presenterDomain}/apps/upgrade/${plan}`

          if (promo) {
            urlRedirect = `${urlRedirect}?promo=${promo}`
          }

          window.location.href = urlRedirect
        } else if (this.isEnableAddinPowerpoint) {
          this.$router.push({
            path: '/apps/presentations',
          })
          office.handleDataAfterLoadOffice()
        } else if (this.integrationPlatform === 'GoogleSlide') {
          this.$router.push({ name: 'IntegrationGoogleSlide' })
        } else {
          this.$router.push({
            path: '/apps/home',
          })
        }
        localStorage.removeItem('blockAudiences')
      } catch (err) {
        this.submitting = false
        if (err.body && err.body.message) {
          this.textError = err.body.message
        } else {
          this.textError = 'default_error'
        }
        Util.mixpanelTracking(
          PRESENTER_LOGIN,
          'Login Button > Submit > Failed',
          JSON.stringify(err.body || err),
        )
      }

      if (this.isCaptcharLoadError) {
        Sentry.captureException(`Presenter login with recaptcha error: ${this.email}`)
      }

      Util.mixpanelTracking(
        PRESENTER_LOGIN,
        'Login Button > Submit',
        '',
      )
    },

    handleLoginWithSso() {
      this.$router.push({
        path: '/pages/sso',
        query: {
          ...this.$route.query,
          ms_teams: this.isFromMSTeams ? true : undefined,
        },
      })
    },

  },
}
</script>

<style lang="scss" scoped>
.separator {
  @apply text-base-90;
  display: flex;
  align-items: center;
  text-align: center;
}

.separator::before,
.separator::after {
  @apply border-base-30 border-b;
  content: '';
  flex: 1;
}

.separator:not(:empty)::before {
  @apply mr-5;
}

.separator:not(:empty)::after {
  @apply ml-5;
}

.text-underline-offset {
  text-underline-offset: 0.25rem;
}
.auth-google-slide {
  .shadow-panel {
    padding-left: 12px;
    padding-right: 12px;
    border-radius: 0;
  }
  .min-h-screen.p-4 {
    padding-left: 0;
    padding-right: 0;
  }
  .auth-logo {
    width: 120px;
  }
}
</style>
